import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  FileCheck,
  FilePlus,
  FileBarChart,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Download,
  Upload,
  Copy,
  Save,
  Calendar,
  User,
  Tag,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Settings,
  Brain,
  FileDown,
  FileUp,
  Layers,
  Target,
  Play,
  X,
  MoreHorizontal,
  HelpCircle,
  TrendingDown,
  Database,
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import TemplateService from '../../services/templateService';
import TemplatePreview from '../settings/TemplatePreview';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import AIService from '../../services/aiService';
import '../../styles/asset-lifecycle-modal.css';

// Custom Dropdown Component for Template Actions
const TemplateActionDropdown = ({
  template,
  onView,
  onEdit,
  onDelete,
  onPreview,
  onGenerate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 200;
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleAction = (action) => {
    setIsOpen(false);
    if (template && (template.id || template._id)) {
      action(template);
    }
  };

  return (
    <div
      className={`relative ${isOpen ? 'z-[9999]' : 'z-auto'}`}
      ref={dropdownRef}
    >
      <button
        ref={buttonRef}
        className={`relative inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm ${
          isOpen ? 'z-[10000]' : 'z-auto'
        }`}
        type="button"
        onClick={handleToggle}
      >
        <MoreHorizontal size={16} className="text-blue-500" />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-[10001]"
          style={{
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${
                  window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8
                }px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => handleAction(onView)}
              >
                <Eye size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                View
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => handleAction(onEdit)}
              >
                <Edit
                  size={16}
                  className="mr-2.5 text-gray-500 flex-shrink-0"
                />
                Edit
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => handleAction(onPreview)}
              >
                <Eye size={16} className="mr-2.5 text-gray-500 flex-shrink-0" />
                Preview
              </button>
            </li>
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 bg-transparent hover:bg-gray-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => handleAction(onGenerate)}
              >
                <Play
                  size={16}
                  className="mr-2.5 text-gray-500 flex-shrink-0"
                />
                Generate
              </button>
            </li>
            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 bg-transparent hover:bg-red-50 border-none cursor-pointer transition-colors text-left"
                onClick={() => handleAction(onDelete)}
              >
                <Trash2
                  size={16}
                  className="mr-2.5 text-red-600 flex-shrink-0"
                />
                Delete
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const ContractTemplates = () => {
  const {
    success,
    error: showError,
    warning,
    info,
    toasts,
    removeToast,
  } = useToast();

  // State management
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateType: 'Word',
    applicableTypes: [],
    placeholders: [],
    sections: [],
    status: 'Draft',
    tags: [],
    settings: {
      includeClauses: true,
      includeDocuments: false,
      defaultOutputFormat: 'Word',
      pageSettings: {
        orientation: 'Portrait',
        margins: { top: 1, bottom: 1, left: 1, right: 1 },
        fontSize: 12,
        fontFamily: 'Arial',
      },
    },
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // AI Generator state
  const [aiFormData, setAiFormData] = useState({
    contractType: '',
    templateName: '',
    requirements: '',
    context: '',
  });
  const [isAIGenerating, setIsAIGenerating] = useState(false);
  const [aiGeneratedTemplate, setAiGeneratedTemplate] = useState(null);

  // Analytics state
  const [analyticsTimeframe, setAnalyticsTimeframe] = useState('30d');

  // Available options
  const [contractTypes, setContractTypes] = useState([]);
  const [templateStatuses, setTemplateStatuses] = useState([]);
  const [statistics, setStatistics] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Load initial data
  useEffect(() => {
    loadTemplates();
    loadContractTypes();
    loadTemplateStatuses();
    loadStatistics();
  }, []);

  // Load templates
  const loadTemplates = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
      };

      const response = await TemplateService.getTemplates(params);
      if (response.success) {
        setTemplates(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        } else {
          setPagination((prev) => ({
            ...prev,
            totalItems: response.data?.length || 0,
            totalPages: Math.ceil(
              (response.data?.length || 0) / prev.itemsPerPage
            ),
          }));
        }
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      showError(error.message || 'Failed to load contract templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Load contract types (dropdown from API)
  const loadContractTypes = async () => {
    try {
      const response = await TemplateService.getContractTypes();
      const raw = response?.data ?? response;
      const list = Array.isArray(raw) ? raw : [];
      const normalized = list.map((t) =>
        typeof t === 'string'
          ? { value: t, label: t }
          : { value: t.value ?? t.code ?? t.label ?? t.name, label: t.label ?? t.name ?? t.value ?? t.code ?? String(t) }
      );
      setContractTypes(normalized);
    } catch (error) {
      console.error('Error loading contract types:', error);
      setContractTypes([]);
    }
  };

  // Load template statuses
  const loadTemplateStatuses = async () => {
    try {
      const response = await TemplateService.getTemplateStatuses();
      if (response.success) {
        setTemplateStatuses(response.data);
      }
    } catch (error) {
      console.error('Error loading template statuses:', error);
    }
  };

  // Load statistics (usage analytics from API)
  const loadStatistics = async () => {
    try {
      const response = await TemplateService.getTemplateStatistics();
      const data = response?.data ?? response;
      setStatistics(data && typeof data === 'object' ? data : null);
    } catch (error) {
      console.error('Error loading statistics:', error);
      setStatistics(null);
    }
  };

  // Refresh handler – refresh templates, contract types dropdown, and analytics
  const handleRefresh = () => {
    loadTemplates();
    loadContractTypes();
    loadStatistics();
  };

  // Mock data for contract templates (fallback)
  const [templateData, setTemplateData] = useState([
    {
      id: 'TMP-001',
      templateName: 'Service Level Agreement (SLA)',
      templateCode: 'SLA-STD',
      category: 'Service Agreement',
      contractType: 'Service',
      description:
        'Standard service level agreement template for IT services and support',
      version: '2.1',
      status: 'Active',
      usageCount: 45,
      lastUsed: '2024-02-15T10:30:00Z',
      createdDate: '2023-01-15',
      lastModified: '2024-01-20T14:20:00Z',
      createdBy: 'Legal Team',
      modifiedBy: 'Legal Team',
      language: 'English',
      jurisdiction: 'United States',
      applicableLaws: ['UCC', 'State Contract Law'],
      keyClauses: [
        'Service Levels',
        'Performance Metrics',
        'Penalties',
        'Termination',
      ],
      requiredFields: [
        'Service Provider',
        'Service Recipient',
        'Service Description',
        'SLA Metrics',
      ],
      optionalFields: [
        'Escalation Procedures',
        'Reporting Requirements',
        'Review Periods',
      ],
      templateSize: '15KB',
      fileFormat: 'DOCX',
      tags: ['service', 'sla', 'it', 'support'],
      notes: 'Most commonly used template for IT service agreements',
    },
    {
      id: 'TMP-002',
      templateName: 'Software License Agreement',
      templateCode: 'SW-LIC',
      category: 'License Agreement',
      contractType: 'License',
      description: 'Comprehensive software licensing agreement template',
      version: '1.8',
      status: 'Active',
      usageCount: 32,
      lastUsed: '2024-02-10T16:45:00Z',
      createdDate: '2023-02-01',
      lastModified: '2024-01-15T09:30:00Z',
      createdBy: 'Legal Team',
      modifiedBy: 'IT Legal',
      language: 'English',
      jurisdiction: 'United States',
      applicableLaws: ['Copyright Law', 'Software Licensing Law'],
      keyClauses: [
        'License Grant',
        'Usage Restrictions',
        'Intellectual Property',
        'Support Terms',
      ],
      requiredFields: [
        'Licensor',
        'Licensee',
        'Software Description',
        'License Type',
      ],
      optionalFields: [
        'Support Services',
        'Training',
        'Customization',
        'Maintenance',
      ],
      templateSize: '22KB',
      fileFormat: 'DOCX',
      tags: ['software', 'license', 'intellectual-property'],
      notes: 'Standard template for software licensing agreements',
    },
    {
      id: 'TMP-003',
      templateName: 'Non-Disclosure Agreement (NDA)',
      templateCode: 'NDA-MUT',
      category: 'Confidentiality',
      contractType: 'NDA',
      description:
        'Mutual non-disclosure agreement for confidential information sharing',
      version: '3.0',
      status: 'Active',
      usageCount: 78,
      lastUsed: '2024-02-12T11:20:00Z',
      createdDate: '2023-01-10',
      lastModified: '2024-02-01T13:15:00Z',
      createdBy: 'Legal Team',
      modifiedBy: 'Legal Team',
      language: 'English',
      jurisdiction: 'United States',
      applicableLaws: ['Trade Secret Law', 'Contract Law'],
      keyClauses: [
        'Confidentiality Obligations',
        'Exclusions',
        'Term',
        'Remedies',
      ],
      requiredFields: [
        'Disclosing Party',
        'Receiving Party',
        'Purpose',
        'Confidential Information',
      ],
      optionalFields: [
        'Return of Information',
        'Survival Period',
        'Governing Law',
      ],
      templateSize: '12KB',
      fileFormat: 'DOCX',
      tags: ['nda', 'confidentiality', 'mutual'],
      notes: 'Most frequently used template for confidentiality agreements',
    },
    {
      id: 'TMP-004',
      templateName: 'Master Service Agreement',
      templateCode: 'MSA-GEN',
      category: 'Service Agreement',
      contractType: 'Service',
      description:
        'Master service agreement template for ongoing service relationships',
      version: '1.5',
      status: 'Active',
      usageCount: 28,
      lastUsed: '2024-02-08T14:30:00Z',
      createdDate: '2023-03-15',
      lastModified: '2024-01-25T10:45:00Z',
      createdBy: 'Legal Team',
      modifiedBy: 'Legal Team',
      language: 'English',
      jurisdiction: 'United States',
      applicableLaws: ['UCC', 'Service Contract Law'],
      keyClauses: [
        'Service Scope',
        'Payment Terms',
        'Performance Standards',
        'Termination',
      ],
      requiredFields: [
        'Service Provider',
        'Client',
        'Service Description',
        'Pricing',
      ],
      optionalFields: [
        'Service Levels',
        'Reporting',
        'Change Management',
        'Dispute Resolution',
      ],
      templateSize: '28KB',
      fileFormat: 'DOCX',
      tags: ['service', 'master', 'ongoing'],
      notes: 'Template for long-term service relationships',
    },
    {
      id: 'TMP-005',
      templateName: 'Employment Agreement',
      templateCode: 'EMP-STD',
      category: 'Employment',
      contractType: 'Employment',
      description: 'Standard employment agreement template for new hires',
      version: '2.3',
      status: 'Active',
      usageCount: 156,
      lastUsed: '2024-02-14T09:15:00Z',
      createdDate: '2023-01-05',
      lastModified: '2024-02-05T16:20:00Z',
      createdBy: 'HR Legal',
      modifiedBy: 'HR Legal',
      language: 'English',
      jurisdiction: 'United States',
      applicableLaws: ['Employment Law', 'Labor Law'],
      keyClauses: ['Position', 'Compensation', 'Benefits', 'Termination'],
      requiredFields: [
        'Employee',
        'Employer',
        'Position',
        'Start Date',
        'Salary',
      ],
      optionalFields: [
        'Benefits',
        'Non-compete',
        'Confidentiality',
        'Probation Period',
      ],
      templateSize: '18KB',
      fileFormat: 'DOCX',
      tags: ['employment', 'hr', 'hiring'],
      notes: 'Standard template for all new employee agreements',
    },
    {
      id: 'TMP-006',
      templateName: 'Vendor Agreement',
      templateCode: 'VEND-STD',
      category: 'Vendor Agreement',
      contractType: 'Vendor',
      description:
        'Standard vendor agreement template for supplier relationships',
      version: '1.2',
      status: 'Draft',
      usageCount: 12,
      lastUsed: '2024-01-30T15:45:00Z',
      createdDate: '2023-04-01',
      lastModified: '2024-02-10T11:30:00Z',
      createdBy: 'Procurement Legal',
      modifiedBy: 'Procurement Legal',
      language: 'English',
      jurisdiction: 'United States',
      applicableLaws: ['UCC', 'Procurement Law'],
      keyClauses: ['Supply Terms', 'Quality Standards', 'Delivery', 'Payment'],
      requiredFields: ['Vendor', 'Buyer', 'Products/Services', 'Pricing'],
      optionalFields: [
        'Quality Control',
        'Delivery Terms',
        'Warranty',
        'Insurance',
      ],
      templateSize: '20KB',
      fileFormat: 'DOCX',
      tags: ['vendor', 'supplier', 'procurement'],
      notes:
        'Template for vendor and supplier agreements - currently under review',
    },
  ]);

  // Calculate statistics from API or fallback from templates list
  const stats = (() => {
    const overview = statistics?.overview ?? statistics;
    if (overview && typeof overview === 'object') {
      return {
        totalTemplates: overview.totalTemplates ?? 0,
        activeTemplates: overview.activeTemplates ?? 0,
        draftTemplates:
          (overview.totalTemplates ?? 0) - (overview.activeTemplates ?? 0),
        totalUsage: overview.totalUsage ?? 0,
        averageUsage:
          (overview.totalTemplates ?? 0) > 0
            ? Math.round(
                (overview.totalUsage ?? 0) / (overview.totalTemplates ?? 1)
              )
            : 0,
        categories: Array.isArray(statistics?.byContractType)
          ? statistics.byContractType.length
          : 0,
      };
    }
    // Fallback: derive from templates when API stats not available
    const total = templates.length;
    const totalUsage = templates.reduce(
      (s, t) => s + (Number(t.usageCount) || 0),
      0
    );
    const active = templates.filter((t) => t.status === 'Active').length;
    return {
      totalTemplates: total,
      activeTemplates: active,
      draftTemplates: total - active,
      totalUsage,
      averageUsage: total > 0 ? Math.round(totalUsage / total) : 0,
      categories: new Set(templates.flatMap((t) => t.applicableTypes || [])).size,
    };
  })();

  const tabs = [
    { id: 'templates', label: 'Templates', icon: <FileText size={18} /> },
    {
      id: 'library',
      label: 'Template Library',
      icon: <FileBarChart size={18} />,
    },
    { id: 'generator', label: 'AI Generator', icon: <Brain size={18} /> },
    {
      id: 'analytics',
      label: 'Usage Analytics',
      icon: <BarChart3 size={18} />,
    },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Template Name',
      width: '250px',
      render: (value, row) => {
        if (!row) return <span className="text-gray-500">N/A</span>;
        const name = row.name || 'Unnamed Template';
        const description = row.description || '';
        return (
          <div>
            <div className="font-semibold text-gray-900 text-sm mb-0.5">
              {name}
            </div>
            {description && (
              <div className="text-gray-500 text-xs">{description}</div>
            )}
            <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: '2px' }}>
              v{row.version || '1.0'}
            </div>
          </div>
        );
      },
    },
    {
      key: 'templateType',
      label: 'Type',
      width: '100px',
      render: (type) => (
        <span style={{ fontSize: '14px', color: '#374151', fontWeight: '500' }}>
          {type || 'Word'}
        </span>
      )
    },
    {
      key: 'applicableTypes',
      label: 'Contract Types',
      width: '150px',
      render: (types, row) => {
        if (!types || !Array.isArray(types) || types.length === 0) {
          return <span className="text-gray-500 text-xs">None</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {types.slice(0, 2).map((type, index) => (
              <span
                key={index}
                className="px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
              >
                {type}
              </span>
            ))}
            {types.length > 2 && (
              <span className="text-xs text-gray-500">
                +{types.length - 2} more
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '100px',
      render: (status) => {
        const statusConfig = {
          Active: {
            bg: 'bg-emerald-100',
            text: 'text-emerald-700',
            dot: 'bg-emerald-500',
            label: 'Active',
          },
          Draft: {
            bg: 'bg-amber-100',
            text: 'text-amber-700',
            dot: 'bg-amber-500',
            label: 'Draft',
          },
          Deprecated: {
            bg: 'bg-red-100',
            text: 'text-red-700',
            dot: 'bg-red-500',
            label: 'Deprecated',
          },
          Inactive: {
            bg: 'bg-gray-100',
            text: 'text-gray-700',
            dot: 'bg-gray-500',
            label: 'Inactive',
          },
        };
        const config = statusConfig[status] || statusConfig['Draft'];
        return (
          <span
            className={`${config.bg} ${config.text} rounded-full px-2 py-1 text-xs font-medium inline-flex items-center gap-1`}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'usageCount',
      label: 'Usage',
      width: '100px',
      render: (count) => (
        <div className="flex items-center gap-1">
          <FileText size={14} className="text-blue-600" />
          <span className="font-medium text-gray-900">{count || 0}</span>
        </div>
      ),
    },
    {
      key: 'lastUsed',
      label: 'Last Used',
      width: '120px',
      render: (date) => {
        if (!date) return <span className="text-gray-500 text-xs">Never</span>;
        try {
          const dateObj = new Date(date);
          if (isNaN(dateObj.getTime()))
            return <span className="text-gray-500 text-xs">N/A</span>;
          return (
            <span className="text-sm text-gray-700">
              {dateObj.toLocaleDateString()}
            </span>
          );
        } catch (e) {
          return <span className="text-gray-500 text-xs">N/A</span>;
        }
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => {
        if (!row || (!row.id && !row._id)) {
          return <span className="text-red-600">Error</span>;
        }
        return (
          <div className="flex justify-start items-center">
            <TemplateActionDropdown
              template={row}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handlePreview}
              onGenerate={handleGenerateDocument}
            />
          </div>
        );
      },
    },
  ];

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);

      // Extract placeholders from file content if it's a text-based file
      if (file.type === 'text/html' || file.type === 'text/plain') {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          const placeholders = TemplateService.extractPlaceholders(content);
          setFormData((prev) => ({
            ...prev,
            placeholders: placeholders,
          }));
        };
        reader.readAsText(file);
      }
    }
  };

  // Handler functions
  const handleView = (template) => {
    setSelectedTemplate(template);
    setShowViewModal(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      templateType: template.templateType,
      applicableTypes: template.applicableTypes,
      placeholders: template.placeholders,
      sections: template.sections,
      status: template.status,
      tags: template.tags,
      settings: template.settings,
    });
    setUploadedFile(null);
    setFormErrors({});
    setShowEditModal(true);
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handleDelete = (template) => {
    if (!template || (!template.id && !template._id)) {
      showError('Invalid template selected for deletion');
      return;
    }
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    setFormData({
      name: '',
      description: '',
      templateType: 'Word',
      applicableTypes: [],
      placeholders: [],
      sections: [],
      status: 'Draft',
      tags: [],
      settings: {
        includeClauses: true,
        includeDocuments: false,
        defaultOutputFormat: 'Word',
        pageSettings: {
          orientation: 'Portrait',
          margins: { top: 1, bottom: 1, left: 1, right: 1 },
          fontSize: 12,
          fontFamily: 'Arial',
        },
      },
    });
    setUploadedFile(null);
    setFormErrors({});
    setShowAddModal(true);
  };

  // Header actions - defined after handlers
  const headerActions = [
    {
      label: '', // Icon-only button
      icon: <HelpCircle size={18} />,
      onClick: () => {
        window.open('/help/contracts/templates.html', '_blank');
      },
      variant: 'outline',
      tooltip: 'Contract Templates Help',
    },
    {
      label: '', // Icon-only button
      icon: (
        <RefreshCw
          size={18}
          className={loading ? 'spinning' : ''}
          style={loading ? { animation: 'spin 1s linear infinite' } : {}}
        />
      ),
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh templates',
      disabled: loading,
    },
    {
      label: 'New Template',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
      tooltip: 'Create a new template',
    },
  ];

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate form data
    const validation = TemplateService.validateTemplateData(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setIsSaving(true);
    try {
      const formattedData = TemplateService.formatTemplateData(formData);

      if (selectedTemplate) {
        // Update existing template
        const templateId = selectedTemplate.id || selectedTemplate._id;
        if (!templateId) {
          showError('Invalid template ID for update');
          return;
        }
        const response = await TemplateService.updateTemplate(
          templateId,
          formattedData,
          uploadedFile
        );
        if (response?.success !== false) {
          success('Template updated successfully');
          setShowEditModal(false);
          setSelectedTemplate(null);
          loadTemplates();
          loadContractTypes();
          loadStatistics();
        } else {
          showError(response.message || 'Failed to update template');
        }
      } else {
        // Create new template
        const response = await TemplateService.createTemplate(
          formattedData,
          uploadedFile
        );
        if (response?.success !== false) {
          success('Template created successfully');
          setShowAddModal(false);
          setFormData({
            name: '',
            description: '',
            templateType: 'Word',
            applicableTypes: [],
            placeholders: [],
            sections: [],
            status: 'Draft',
            tags: [],
            settings: {
              includeClauses: true,
              includeDocuments: false,
              defaultOutputFormat: 'Word',
              pageSettings: {
                orientation: 'Portrait',
                margins: { top: 1, bottom: 1, left: 1, right: 1 },
                fontSize: 12,
                fontFamily: 'Arial',
              },
            },
          });
          setUploadedFile(null);
          loadTemplates();
          loadContractTypes();
          loadStatistics();
        } else {
          showError(response.message || 'Failed to create template');
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete || (!templateToDelete.id && !templateToDelete._id)) {
      showError('Invalid template selected for deletion');
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      return;
    }

    setIsDeleting(true);
    try {
      const templateId = templateToDelete.id || templateToDelete._id;
      const response = await TemplateService.deleteTemplate(templateId);
      if (response?.success !== false) {
        success('Template deleted successfully');
        setShowDeleteModal(false);
        setTemplateToDelete(null);
        loadTemplates();
        loadContractTypes();
        loadStatistics();
      } else {
        showError(response.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      showError(error.message || 'Failed to delete template');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateDocument = async (template) => {
    setIsGenerating(true);
    try {
      const sampleData = TemplateService.getSampleContractData();
      const response = await TemplateService.generateDocument(
        template.id,
        sampleData,
        'Word'
      );
      if (response.success) {
        success('Document generated successfully');
        // Download the generated file
        const downloadUrl = TemplateService.getFileDownloadUrl(
          response.data.documentUrl
        );
        if (downloadUrl) {
          await TemplateService.downloadFile(
            downloadUrl,
            response.data.fileName
          );
        }
        loadTemplates(); // Refresh to update usage count
      }
    } catch (error) {
      console.error('Error generating document:', error);
      showError('Failed to generate document');
    } finally {
      setIsGenerating(false);
    }
  };

  // Fill Test Data for AI Generator
  const fillTestData = () => {
    setAiFormData({
      contractType: 'Service Agreement',
      templateName: 'Standard Service Agreement Template',
      requirements:
        'Monthly service delivery, 24/7 support, SLA compliance, performance metrics, payment terms, termination clauses',
      context:
        'This template should include standard service agreement terms including scope of services, payment terms, intellectual property rights, confidentiality, liability limitations, and dispute resolution mechanisms.',
    });
    success('Test data loaded! Ready for AI generation.');
  };

  // AI Generator handlers
  const handleAIGenerate = async () => {
    if (!aiFormData.contractType || !aiFormData.templateName) {
      showError('Please fill in Contract Type and Template Name');
      return;
    }

    try {
      setIsAIGenerating(true);

      // Build prompt for AI generation
      const prompt = `Generate a comprehensive contract template for a ${
        aiFormData.contractType
      } with the following requirements:

Template Name: ${aiFormData.templateName}
Contract Type: ${aiFormData.contractType}
Key Requirements: ${aiFormData.requirements || 'Standard contract requirements'}
Additional Context: ${aiFormData.context || 'None'}

Please generate a professional contract template with:
- Proper sections and structure
- Standard legal clauses
- Placeholders for dynamic data
- Professional formatting
- Industry best practices`;

      // Call backend AI service
      let aiResponse;
      try {
        // Build the prompt for template generation
        const templatePrompt = `Generate a contract template for ${
          aiFormData.contractType
        }. 
Template Name: ${aiFormData.templateName}
Requirements: ${aiFormData.requirements || 'Standard contract requirements'}
Context: ${aiFormData.context || 'None'}

Generate a professional template structure with placeholders and sections.`;

        // Use the AI service - it expects (prompt, contractType)
        aiResponse = await AIService.generateContract(
          templatePrompt,
          aiFormData.contractType
        );

        // If the response doesn't have the expected structure, transform it
        if (!aiResponse.data) {
          aiResponse = {
            success: true,
            data: {
              contractContent:
                aiResponse.response ||
                aiResponse.contractContent ||
                `<h1>${aiFormData.templateName}</h1><p>AI-generated ${aiFormData.contractType} template</p>`,
              summary: `AI-generated ${aiFormData.contractType} template`,
              sections: [],
            },
          };
        }
      } catch (apiError) {
        console.warn('AI service call failed, using fallback:', apiError);
        // Fallback to local generation
        aiResponse = {
          success: true,
          data: {
            contractContent: `<h1>${aiFormData.templateName}</h1><p>AI-generated ${aiFormData.contractType} template</p>`,
            summary: `AI-generated ${aiFormData.contractType} template`,
            sections: [],
          },
        };
      }

      // Create template structure from AI response
      const generatedTemplate = {
        id: 'ai-generated-' + Date.now(),
        name: aiFormData.templateName,
        description:
          aiResponse.data?.summary ||
          `AI-generated ${aiFormData.contractType} template based on your requirements`,
        templateType: 'HTML',
        applicableTypes: [aiFormData.contractType],
        status: 'Draft',
        version: '1.0',
        placeholders: generateAIPlaceholders(aiFormData.contractType),
        sections:
          aiResponse.data?.sections ||
          generateAISections(aiFormData.contractType),
        content: aiResponse.data?.contractContent || '',
        settings: {
          includeClauses: true,
          includeDocuments: false,
          defaultOutputFormat: 'HTML',
          pageSettings: {
            orientation: 'Portrait',
            margins: { top: 1, bottom: 1, left: 1, right: 1 },
            fontSize: 12,
            fontFamily: 'Arial',
          },
        },
        usageCount: 0,
        createdAt: new Date().toISOString(),
        createdBy: 'AI Generator',
        aiGenerated: true,
        metadata: aiResponse.data?.metadata || {},
      };

      setAiGeneratedTemplate(generatedTemplate);
      success('Template generated successfully using AI!');
    } catch (error) {
      console.error('Error generating AI template:', error);
      showError(error.message || 'Error generating template');
    } finally {
      setIsAIGenerating(false);
    }
  };

  const handleAIPreview = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const handleAISave = async (template) => {
    try {
      setIsSaving(true);

      // Save the AI-generated template
      const response = await TemplateService.createTemplate(template);

      if (response.success) {
        success('Template saved successfully!');
        setAiGeneratedTemplate(null);
        setAiFormData({
          contractType: '',
          templateName: '',
          requirements: '',
          context: '',
        });
        await loadTemplates(); // Refresh templates list
      } else {
        showError('Error saving template: ' + response.message);
      }
    } catch (error) {
      console.error('Error saving AI template:', error);
      showError('Error saving template');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper functions for AI generation
  const generateAIPlaceholders = (contractType) => {
    const basePlaceholders = [
      {
        name: 'contractId',
        label: 'Contract ID',
        type: 'text',
        required: true,
      },
      {
        name: 'contractName',
        label: 'Contract Name',
        type: 'text',
        required: true,
      },
      {
        name: 'effectiveDate',
        label: 'Effective Date',
        type: 'date',
        required: true,
      },
      {
        name: 'partyA.name',
        label: 'Party A Name',
        type: 'text',
        required: true,
      },
      {
        name: 'partyA.address',
        label: 'Party A Address',
        type: 'text',
        required: true,
      },
      {
        name: 'partyB.name',
        label: 'Party B Name',
        type: 'text',
        required: true,
      },
      {
        name: 'partyB.address',
        label: 'Party B Address',
        type: 'text',
        required: true,
      },
    ];

    switch (contractType) {
      case 'NDA':
      case 'Confidentiality':
        return [
          ...basePlaceholders,
          {
            name: 'confidentialityPeriod',
            label: 'Confidentiality Period',
            type: 'text',
            required: true,
          },
          {
            name: 'noticePeriod',
            label: 'Notice Period',
            type: 'text',
            required: true,
          },
          {
            name: 'governingLaw',
            label: 'Governing Law',
            type: 'text',
            required: true,
          },
        ];
      case 'Service Agreement':
      case 'MSA':
        return [
          ...basePlaceholders,
          {
            name: 'contractValue',
            label: 'Contract Value',
            type: 'currency',
            required: true,
          },
          { name: 'currency', label: 'Currency', type: 'text', required: true },
          {
            name: 'paymentTerms',
            label: 'Payment Terms',
            type: 'text',
            required: true,
          },
          {
            name: 'contractTerm',
            label: 'Contract Term',
            type: 'text',
            required: true,
          },
        ];
      default:
        return basePlaceholders;
    }
  };

  const generateAISections = (contractType) => {
    const baseSections = [
      {
        name: 'Parties',
        content: 'This section identifies the parties to the agreement',
      },
      {
        name: 'Purpose',
        content: 'This section describes the purpose of the agreement',
      },
      {
        name: 'Terms and Conditions',
        content: 'This section outlines the terms and conditions',
      },
      {
        name: 'Signatures',
        content: 'This section contains signature blocks for both parties',
      },
    ];

    switch (contractType) {
      case 'NDA':
      case 'Confidentiality':
        return [
          ...baseSections,
          {
            name: 'Confidential Information',
            content:
              'This section defines what constitutes confidential information',
          },
          {
            name: 'Obligations',
            content:
              'This section outlines the obligations of the receiving party',
          },
          {
            name: 'Term',
            content: 'This section specifies the duration of the agreement',
          },
        ];
      case 'Service Agreement':
      case 'MSA':
        return [
          ...baseSections,
          {
            name: 'Scope of Services',
            content: 'This section describes the services to be provided',
          },
          {
            name: 'Compensation',
            content: 'This section outlines payment terms and billing',
          },
          {
            name: 'Intellectual Property',
            content: 'This section addresses IP ownership and licensing',
          },
        ];
      default:
        return baseSections;
    }
  };

  // Analytics helper functions
  const getMostUsedTemplate = () => {
    if (templates.length === 0) return null;
    return templates.reduce((max, template) =>
      (template.usageCount || 0) > (max.usageCount || 0) ? template : max
    );
  };

  const getTopTemplates = () => {
    return [...templates]
      .sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0))
      .filter((template) => template.status === 'Active');
  };

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Contract Templates Management"
        description="Create, manage, and organize contract templates for efficient contract generation"
        actions={headerActions}
      />

      {/* KPI Cards – StatCard reference styling */}
      <div className="grid-modern-3 gap-6 mt-6 ml-8 mr-8">
        {/* Total Templates */}
        <div className="relative border border-[var(--border-color)] bg-white">
          <div className="h-1 w-full bg-[var(--primary-color)]" />
          <div className="relative flex items-center gap-3 p-4">
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
              <TrendingUp size={14} /> +0%
            </div>
            <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
              <FileText size={20} style={{ color: '#2D7DD2' }} />
            </div>
            <div>
              <div className="text-[20px] font-bold text-[var(--text-primary)]">
                {stats.totalTemplates}
              </div>
              <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Total Templates
              </div>
            </div>
          </div>
        </div>

        {/* Active Templates */}
        <div className="relative border border-[var(--border-color)] bg-white">
          <div className="h-1 w-full bg-[var(--primary-color)]" />
          <div className="relative flex items-center gap-3 p-4">
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
              <TrendingUp size={14} /> +0%
            </div>
            <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
              <CheckCircle size={20} style={{ color: '#2D7DD2' }} />
            </div>
            <div>
              <div className="text-[20px] font-bold text-[var(--text-primary)]">
                {stats.activeTemplates}
              </div>
              <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Active Templates
              </div>
            </div>
          </div>
        </div>

        {/* Total Usage */}
        <div className="relative border border-[var(--border-color)] bg-white">
          <div className="h-1 w-full bg-[var(--primary-color)]" />
          <div className="relative flex items-center gap-3 p-4">
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
              <TrendingUp size={14} /> +0%
            </div>
            <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
              <TrendingUp size={20} style={{ color: '#2D7DD2' }} />
            </div>
            <div>
              <div className="text-[20px] font-bold text-[var(--text-primary)]">
                {stats.totalUsage}
              </div>
              <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Total Usage
              </div>
            </div>
          </div>
        </div>

        {/* Draft Templates */}
        <div className="relative border border-[var(--border-color)] bg-white">
          <div className="h-1 w-full bg-[var(--primary-color)]" />
          <div className="relative flex items-center gap-3 p-4">
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
              <TrendingUp size={14} /> +0%
            </div>
            <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
              <FilePlus size={20} style={{ color: '#2D7DD2' }} />
            </div>
            <div>
              <div className="text-[20px] font-bold text-[var(--text-primary)]">
                {stats.draftTemplates}
              </div>
              <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Draft Templates
              </div>
            </div>
          </div>
        </div>

        {/* Avg. Usage */}
        <div className="relative border border-[var(--border-color)] bg-white">
          <div className="h-1 w-full bg-[var(--primary-color)]" />
          <div className="relative flex items-center gap-3 p-4">
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
              <TrendingUp size={14} /> +0%
            </div>
            <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
              <BarChart3 size={20} style={{ color: '#2D7DD2' }} />
            </div>
            <div>
              <div className="text-[20px] font-bold text-[var(--text-primary)]">
                {Math.round(stats.averageUsage)}
              </div>
              <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Avg. Usage
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="relative border border-[var(--border-color)] bg-white">
          <div className="h-1 w-full bg-[var(--primary-color)]" />
          <div className="relative flex items-center gap-3 p-4">
            <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
              <TrendingUp size={14} /> +0%
            </div>
            <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
              <Layers size={20} style={{ color: '#2D7DD2' }} />
            </div>
            <div>
              <div className="text-[20px] font-bold text-[var(--text-primary)]">
                {stats.categories}
              </div>
              <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                Categories
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-container w-full max-w-full">
        {/* `Tabbed` Navigation */}
        <div className="project-tabs">
          <div className="project-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${
                  activeTab === tab.id ? 'active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">{tab.icon}</div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content - DataTable */}
        {activeTab === 'templates' && (
          <div className="page-tab-content mt-6">
            <DataTable
              data={templates}
              columns={columns}
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={pagination.itemsPerPage}
              showActions={false}
              showCheckboxes={false}
              searchPlaceholder="Search templates by name or description..."
              emptyMessage="No templates found. Click 'New Template' to get started."
              loading={loading}
            />
          </div>
        )}

        {activeTab === 'library' && (
          <div className="page-tab-content mt-6 !p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
                  Template Library
                </h2>
                <p className="text-sm text-[var(--text-secondary)] m-0">
                  Browse and manage your contract template collection
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[var(--primary-color)] text-white rounded text-sm font-medium cursor-pointer hover:bg-white hover:text-[var(--primary-color)] hover:border hover:border-[var(--primary-color)]"
                >
                  <Plus size={16} />
                  Add Template
                </button>
              </div>
            </div>

            {/* Template Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 px-6">
              {contractTypes.map((type) => {
                const typeTemplates = templates.filter((t) =>
                  t.applicableTypes.includes(type.value)
                );
                return (
                  <div
                    key={type.value}
                    className="border border-[var(--border-color)] p-6 bg-white cursor-pointer transition-all duration-200 hover:bg-[var(--accent-color)] hover:border-blue-600 hover:shadow-lg shadow-sm rounded-lg flex flex-col"
                  >
                    <div className="flex items-start gap-4 ">
                      <div className="w-11 h-11 rounded-md bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                        <FileText size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-[var(--text-primary)] m-0 leading-tight">
                          {type.label}
                        </h3>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                          {typeTemplates.length} templates
                        </p>
                      </div>
                    </div>

                    {typeTemplates.length > 0 ? (
                      <div className="mt-auto space-y-2">
                        {typeTemplates.slice(0, 3).map((template) => (
                          <div
                            key={template.id}
                            className="flex items-center gap-3 px-3 py-2 bg-[var(--secondary-color)] text-xs text-[var(--text-primary)] rounded-md"
                          >
                            <FileText
                              size={12}
                              className="text-[var(--text-secondary)] shrink-0"
                            />
                            <span className="flex-1 truncate">
                              {template.name}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                                template.status === 'Active'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-[var(--accent-color)] text-[var(--text-secondary)]'
                              }`}
                            >
                              {template.status}
                            </span>
                          </div>
                        ))}

                        {typeTemplates.length > 3 && (
                          <p className="text-xs text-[var(--text-secondary)] pt-1 text-center">
                            +{typeTemplates.length - 3} more templates
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 !m-0 text-center flex-1 flex items-center justify-center">
                        No templates available
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Recent Templates */}
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-3">
                Recent Templates
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 px-6">
                {templates.slice(0, 6).map((template) => (
                  <div
                    key={template.id}
                    className="border border-[var(--border-color)] p-4 bg-white cursor-pointer transition-all duration-150 hover:border-blue-600 hover:shadow-md shadow-sm rounded-md relative"
                  >
                    {/* Top row */}
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                        <FileText size={16} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-[var(--text-primary)] leading-snug truncate">
                          {template.name}
                        </h4>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                          {template.templateType} • v{template.version}
                        </p>
                      </div>
                    </div>

                    {/* Applicable types – top right */}
                    <div className="absolute top-4 right-4 flex flex-wrap gap-1">
                      {template.applicableTypes
                        .slice(0, 2)
                        .map((type, index) => (
                          <span
                            key={index}
                            className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-600 whitespace-nowrap"
                          >
                            {type}
                          </span>
                        ))}
                      {template.applicableTypes.length > 2 && (
                        <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-[var(--accent-color)] text-[var(--text-secondary)] whitespace-nowrap">
                          +{template.applicableTypes.length - 2}
                        </span>
                      )}
                    </div>

                    {/* Bottom row – pulled up */}
                    <div className="flex justify-between items-center text-xs text-[var(--text-secondary)] mt-2">
                      <span>{template.usageCount || 0} uses</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${
                          template.status === 'Active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-[var(--accent-color)] text-[var(--text-secondary)]'
                        }`}
                      >
                        {template.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'generator' && (
          <div className="page-tab-content mt-6 px-6">
            <div className="flex justify-between items-center pt-6 pl-6 pr-6 pb-1">
              <div>
                <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2 flex items-center gap-2">
                  <Brain size={24} className="text-blue-600" />
                  AI Template Generator
                </h2>
                <p className="text-sm text-[var(--text-secondary)] m-0">
                  Generate intelligent contract templates using AI
                </p>
              </div>
              <button
                type="button"
                onClick={fillTestData}
                className="flex items-center gap-2 px-4 py-2.5 bg-white text-[var(--text-primary)] border border-[var(--border-color)] rounded text-sm font-medium cursor-pointer hover:bg-gray-50"
              >
                <Database size={16} />
                Fill Test Data
              </button>
            </div>
            {/* AI Generator Form */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-8 pb-0">
              {/* Input Section */}
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: '0 0 16px 0',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Template Requirements
                </h3>

                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '6px',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Contract Type
                  </label>
                  <select
                    value={aiFormData.contractType}
                    onChange={(e) =>
                      setAiFormData((prev) => ({
                        ...prev,
                        contractType: e.target.value,
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      backgroundColor: 'white',
                    }}
                  >
                    <option value="">Select contract type</option>
                    {contractTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '6px',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Template Name
                  </label>
                  <input
                    type="text"
                    value={aiFormData.templateName}
                    onChange={(e) =>
                      setAiFormData((prev) => ({
                        ...prev,
                        templateName: e.target.value,
                      }))
                    }
                    placeholder="e.g., Standard NDA Template"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '6px',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Key Requirements
                  </label>
                  <textarea
                    value={aiFormData.requirements}
                    onChange={(e) =>
                      setAiFormData((prev) => ({
                        ...prev,
                        requirements: e.target.value,
                      }))
                    }
                    placeholder="Describe the key requirements for this template..."
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '6px',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Additional Context
                  </label>
                  <textarea
                    value={aiFormData.context}
                    onChange={(e) =>
                      setAiFormData((prev) => ({
                        ...prev,
                        context: e.target.value,
                      }))
                    }
                    placeholder="Any specific clauses, terms, or legal requirements..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontFamily: 'var(--font-family)',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <button
                  onClick={handleAIGenerate}
                  disabled={
                    isAIGenerating ||
                    !aiFormData.contractType ||
                    !aiFormData.templateName
                  }
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: isAIGenerating ? '#9ca3af' : '#4678be',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: isAIGenerating ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  {isAIGenerating ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Brain size={16} />
                      Generate Template
                    </>
                  )}
                </button>
              </div>

              {/* Preview Section */}
              <div>
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    margin: '0 0 16px 0',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Generated Template
                </h3>

                {aiGeneratedTemplate ? (
                  <div
                    style={{
                      border: '1px solid var(--border-color)',
                      borderRadius: '0',
                      padding: '16px',
                      backgroundColor: 'var(--secondary-color)',
                      maxHeight: '400px',
                      overflow: 'auto',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                      }}
                    >
                      <h4
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--text-primary)',
                          margin: 0,
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        {aiGeneratedTemplate.name}
                      </h4>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '0',
                          fontSize: '11px',
                          fontWeight: '500',
                          backgroundColor: '#d1fae5',
                          color: '#065f46',
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        Generated
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        marginBottom: '12px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <p>
                        <strong>Type:</strong>{' '}
                        {aiGeneratedTemplate.templateType}
                      </p>
                      <p>
                        <strong>Contract Types:</strong>{' '}
                        {aiGeneratedTemplate.applicableTypes.join(', ')}
                      </p>
                      <p>
                        <strong>Placeholders:</strong>{' '}
                        {aiGeneratedTemplate.placeholders?.length || 0}
                      </p>
                    </div>

                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-primary)',
                        lineHeight: '1.5',
                        marginBottom: '16px',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      <strong>Description:</strong>
                      <p style={{ margin: '4px 0 0 0' }}>
                        {aiGeneratedTemplate.description}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleAIPreview(aiGeneratedTemplate)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'var(--accent-color)',
                          color: 'var(--text-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '0',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        <Eye size={12} />
                        Preview
                      </button>
                      <button
                        onClick={() => handleAISave(aiGeneratedTemplate)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#4678be',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontFamily: 'var(--font-family)',
                        }}
                      >
                        <Save size={12} />
                        Save Template
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      border: '2px dashed var(--border-color)',
                      borderRadius: '0',
                      padding: '40px',
                      textAlign: 'center',
                      backgroundColor: 'var(--secondary-color)',
                    }}
                  >
                    <Brain
                      size={32}
                      color="var(--text-muted)"
                      style={{ marginBottom: '12px' }}
                    />
                    <p
                      style={{
                        fontSize: '14px',
                        color: 'var(--text-secondary)',
                        margin: '0 0 8px 0',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      No template generated yet
                    </p>
                    <p
                      style={{
                        fontSize: '12px',
                        color: 'var(--text-muted)',
                        margin: 0,
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      Fill in the requirements and click "Generate Template"
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* AI Features */}
            <div>
              <h3 className="text-base font-semibold text-[var(--text-primary)] p-8 pb-0">
                AI-Powered Features
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-8 pl-8 pr-8">
                <div className="border border-[var(--border-color)] p-4 bg-white shadow-sm rounded-2xl">
                  <div className="flex items-center gap-2 mb-2 ">
                    <Brain size={16} className="text-blue-600" />
                    <h4 className="text-sm font-semibold text-[var(--text-primary)] m-0">
                      Smart Generation
                    </h4>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] m-0">
                    AI analyzes requirements and generates comprehensive
                    templates
                  </p>
                </div>

                <div className="border border-[var(--border-color)] p-4 bg-white shadow-sm rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={16} className="text-blue-600" />
                    <h4 className="text-sm font-semibold text-[var(--text-primary)] m-0">
                      Auto Placeholders
                    </h4>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] m-0">
                    Automatically creates relevant placeholders for dynamic
                    content
                  </p>
                </div>

                <div className="border border-[var(--border-color)] p-4 bg-white shadow-sm rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={16} className="text-blue-600" />
                    <h4 className="text-sm font-semibold text-[var(--text-primary)] m-0">
                      Legal Compliance
                    </h4>
                  </div>
                  <p className="text-xs text-[var(--text-secondary)] m-0">
                    Ensures templates include standard legal clauses and terms
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="bg-white p-8 rounded-lg shadow-sm mt-6">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px 0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <BarChart3 size={24} color="#007B8F" />
                  Usage Analytics
                </h2>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0,
                  }}
                >
                  Track template usage and performance metrics
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <select
                  value={analyticsTimeframe}
                  onChange={(e) => setAnalyticsTimeframe(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily:
                      'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                    backgroundColor: 'white',
                  }}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
              </div>
            </div>

            {/* Analytics Overview Cards */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3 mb-4">
              {/* Total Usage */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-[40px_1fr] gap-3 items-center">
                  <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-[#007B8F20] text-[#007B8F]">
                    <FileText size={18} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-gray-800">
                      Total Usage
                    </div>
                    <div className="text-xl font-bold text-[#007B8F]">
                      {statistics?.totalUsage || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Most Used */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-[40px_1fr] gap-3 items-center">
                  <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-[#10b98120] text-[#10b981]">
                    <TrendingUp size={18} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-gray-800">
                      Most Used
                    </div>
                    <div className="text-xl font-bold text-[#10b981]">
                      {getMostUsedTemplate()?.usageCount || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Avg Usage */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-[40px_1fr] gap-3 items-center">
                  <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-[#f59e0b20] text-[#f59e0b]">
                    <Target size={18} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-gray-800">
                      Avg. Usage
                    </div>
                    <div className="text-xl font-bold text-[#f59e0b]">
                      {statistics?.averageUsage || 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Templates */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="grid grid-cols-[40px_1fr] gap-3 items-center">
                  <div className="h-9 w-9 flex items-center justify-center rounded-lg bg-[#8b5cf620] text-[#8b5cf6]">
                    <Layers size={18} />
                  </div>
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-gray-800">
                      Active Templates
                    </div>
                    <div className="text-xl font-bold text-[#8b5cf6]">
                      {templates.filter((t) => t.status === 'Active').length}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '24px',
                marginBottom: '32px',
              }}
            >
              {/* Usage Trend Chart */}
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  // alignItems: 'center',
                  // textAlign: 'center',
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 16px 0',
                  }}
                >
                  Usage Trend
                </h3>

                <div
                  style={{
                    height: '200px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '2px dashed #d1d5db',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <BarChart3
                      size={32}
                      color="#9ca3af"
                      style={{ marginBottom: '8px' }}
                    />
                    <p
                      style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        margin: 0,
                      }}
                    >
                      Usage trend chart
                    </p>
                    <p
                      style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                        margin: '4px 0 0 0',
                      }}
                    >
                      Chart visualization coming soon
                    </p>
                  </div>
                </div>
              </div>

              {/* Template Performance */}
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: 'white',
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 16px 0',
                  }}
                >
                  Top Templates
                </h3>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  {getTopTemplates()
                    .slice(0, 5)
                    .map((template, index) => (
                      <div
                        key={template.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '8px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                        }}
                      >
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: index < 3 ? '#007B8F' : '#6b7280',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: '#1f2937',
                              margin: '0 0 2px 0',
                            }}
                          >
                            {template.name}
                          </p>
                          <p
                            style={{
                              fontSize: '10px',
                              color: '#6b7280',
                              margin: 0,
                            }}
                          >
                            {template.usageCount || 0} uses
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>

            {/* Template Usage Details */}
            <div
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '20px',
                backgroundColor: 'white',
              }}
            >
              <h3
                style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 16px 0',
                }}
              >
                Template Usage Details
              </h3>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '16px',
                }}
              >
                {templates.map((template) => (
                  <div
                    key={template.id}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px',
                      }}
                    >
                      <div>
                        <h4
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#1f2937',
                            margin: '0 0 4px 0',
                          }}
                        >
                          {template.name}
                        </h4>
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            margin: 0,
                          }}
                        >
                          {template.templateType} • v{template.version}
                        </p>
                      </div>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '500',
                          backgroundColor:
                            template.status === 'Active'
                              ? '#d1fae5'
                              : '#f3f4f6',
                          color:
                            template.status === 'Active'
                              ? '#065f46'
                              : '#6b7280',
                        }}
                      >
                        {template.status}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                        }}
                      >
                        Usage Count
                      </span>
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#1f2937',
                        }}
                      >
                        {template.usageCount || 0}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                        }}
                      >
                        Last Used
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                        }}
                      >
                        {template.lastUsed
                          ? new Date(template.lastUsed).toLocaleDateString()
                          : 'Never'}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                        }}
                      >
                        Created
                      </span>
                      <span
                        style={{
                          fontSize: '12px',
                          color: '#6b7280',
                        }}
                      >
                        {new Date(template.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Template Modal */}
        {(showAddModal || showEditModal) && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              animation: 'fadeIn 0.2s ease-in-out',
            }}
          >
            <div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                width: '100%',
                maxWidth: '600px',
                height: '100vh',
                backgroundColor: 'white',
                boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column',
                animation: 'slideInFromRight 0.3s ease-out',
                overflow: 'hidden',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with blue theme */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '20px 24px',
                  backgroundColor: '#4678be',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <h2
                  style={{
                    fontSize: '20px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0,
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {showAddModal ? 'Add New Template' : 'Edit Template'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedTemplate(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'white',
                  }}
                >
                  <X size={20} />
                </button>
              </div>

              <form
                onSubmit={handleFormSubmit}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '24px',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                    marginBottom: '24px',
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange('name', e.target.value)
                      }
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: `1px solid ${
                          formErrors.name ? '#ef4444' : '#d1d5db'
                        }`,
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                      }}
                      placeholder="Enter template name"
                    />
                    {formErrors.name && (
                      <p
                        style={{
                          color: '#ef4444',
                          fontSize: '12px',
                          margin: '4px 0 0 0',
                        }}
                      >
                        {formErrors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Template Type
                    </label>
                    <select
                      value={formData.templateType}
                      onChange={(e) =>
                        handleInputChange('templateType', e.target.value)
                      }
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value="Word">Word Document</option>
                      <option value="PDF">PDF Document</option>
                      <option value="HTML">HTML Template</option>
                    </select>
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Contract Types *
                    </label>
                    <select
                      multiple
                      value={formData.applicableTypes}
                      onChange={(e) => {
                        const selected = Array.from(
                          e.target.selectedOptions,
                          (option) => option.value
                        );
                        handleInputChange('applicableTypes', selected);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: `1px solid ${
                          formErrors.applicableTypes ? '#ef4444' : '#d1d5db'
                        }`,
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                        backgroundColor: 'white',
                        minHeight: '80px',
                      }}
                    >
                      {contractTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    {formErrors.applicableTypes && (
                      <p
                        style={{
                          color: '#ef4444',
                          fontSize: '12px',
                          margin: '4px 0 0 0',
                        }}
                      >
                        {formErrors.applicableTypes}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        handleInputChange('status', e.target.value)
                      }
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                        backgroundColor: 'white',
                      }}
                    >
                      {templateStatuses.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange('description', e.target.value)
                      }
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontFamily: 'var(--font-family)',
                        minHeight: '80px',
                        resize: 'vertical',
                      }}
                      placeholder="Enter template description"
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '6px',
                      }}
                    >
                      Template File
                    </label>
                    <div
                      style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '0',
                        padding: '20px',
                        textAlign: 'center',
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        accept=".docx,.doc,.pdf,.html"
                        style={{ display: 'none' }}
                        id="template-file-upload"
                      />
                      <label
                        htmlFor="template-file-upload"
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Upload size={24} color="#6b7280" />
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>
                          {uploadedFile
                            ? uploadedFile.name
                            : 'Click to upload template file'}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                          Supports .docx, .doc, .pdf, .html files
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    paddingTop: '20px',
                    borderTop: '1px solid #e5e7eb',
                    flexShrink: 0,
                    marginTop: 'auto',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      setSelectedTemplate(null);
                    }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: isSaving ? '#9ca3af' : '#4678be',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font-family)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {isSaving ? (
                      <>
                        <RefreshCw size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        {showAddModal ? 'Create Template' : 'Update Template'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      {/* View Template Modal */}
      {showViewModal && selectedTemplate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'fadeIn 0.2s ease-in-out'
        }}
        onClick={() => {
          setShowViewModal(false);
          setSelectedTemplate(null);
        }}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            animation: 'slideIn 0.3s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              backgroundColor: '#4678be',
              color: 'white',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'white',
                margin: 0,
                fontFamily: 'var(--font-family)'
              }}>
                View Template: {selectedTemplate.name}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedTemplate(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: 'white'
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={{ padding: '24px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '20px',
                marginBottom: '24px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Template Name
                  </label>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937'
                  }}>
                    {selectedTemplate.name}
                  </div>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Type
                  </label>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {selectedTemplate.templateType || 'Word'}
                  </div>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Status
                  </label>
                  <div>
                    <span style={{
                      backgroundColor: selectedTemplate.status === 'Active' ? '#d1fae5' : selectedTemplate.status === 'Draft' ? '#fef3c7' : '#fee2e2',
                      color: selectedTemplate.status === 'Active' ? '#10b981' : selectedTemplate.status === 'Draft' ? '#f59e0b' : '#ef4444',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {selectedTemplate.status || 'Draft'}
                    </span>
                  </div>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Version
                  </label>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {selectedTemplate.version || '1.0'}
                  </div>
                </div>
                
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Description
                  </label>
                  <div style={{
                    fontSize: '14px',
                    color: '#374151',
                    lineHeight: '1.6'
                  }}>
                    {selectedTemplate.description || 'No description provided'}
                  </div>
                </div>
                
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Applicable Contract Types
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedTemplate.applicableTypes && selectedTemplate.applicableTypes.length > 0 ? (
                      selectedTemplate.applicableTypes.map((type, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: '#e0e7ff',
                            color: '#1e40af'
                          }}
                        >
                          {type}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '14px' }}>None specified</span>
                    )}
                  </div>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Usage Count
                  </label>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#1f2937'
                  }}>
                    {selectedTemplate.usageCount || 0}
                  </div>
                </div>
                
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#6b7280',
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Last Used
                  </label>
                  <div style={{
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    {selectedTemplate.lastUsed ? new Date(selectedTemplate.lastUsed).toLocaleDateString() : 'Never'}
                  </div>
                </div>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                paddingTop: '20px',
                borderTop: '1px solid #e5e7eb',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedTemplate(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(selectedTemplate);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4678be',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)'
                  }}
                >
                  Edit Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Preview Modal */}
      <TemplatePreview
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        template={selectedTemplate}
        onGenerate={handleGenerateDocument}
      />

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Contract Template"
          message={`Are you sure you want to delete template "${templateToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />
      </div>
    </>
  );
};

export default ContractTemplates;
